import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/nova/components';

import type { Palette } from '../../banner-kit-api';
import { ColorField } from './SchemaForm';
import * as S from './PlayableComposer.styles';

export interface BrandThemeFieldsProps {
  palette: Palette;
  /** The brand's website theme was read (the section is "Brand theme" with Reset; else plain "Colours"). */
  themed: boolean;
  /** The ad already carries the theme (Reset would change nothing). */
  onTheme: boolean;
  /** What the ad is, for the hint: a mini-game, or a banner (an HTML5 banner or an Image Banners kit). */
  ad: 'game' | 'banner';
  /** No edits (a run is starting or in flight). */
  locked: boolean;
  onPalette: (key: keyof Palette, value: string) => void;
  onReset: () => void;
}

/**
 * An ad composer's colours: the brand palette as four editable swatches, titled "Brand theme" with a Reset to brand
 * theme when the brand's website theme is known (the ad starts from it: palette, CTA button and fonts). Shared by the
 * mini-game, HTML5 banner and Image Banners kit composers.
 */
export const BrandThemeFields = ({
  palette,
  themed,
  onTheme,
  ad,
  locked,
  onPalette,
  onReset,
}: BrandThemeFieldsProps): React.ReactElement => {
  const { t } = useTranslation();
  return (
    <S.Section>
      <S.SectionHead>
        {themed ? <S.SectionTitle text="Brand theme" /> : <S.SectionTitle text="Colours" />}
        {themed ? (
          <Button
            variant="text"
            label={t('Reset to brand theme')}
            onClick={() => {
              if (!locked) onReset();
            }}
            disabled={locked || onTheme}
          />
        ) : null}
      </S.SectionHead>
      {themed && ad === 'game' ? (
        <S.Hint text="Your website's colours and button style, so the game looks like your site. You can still change them." />
      ) : themed ? (
        <S.Hint text="Your website's colours and button style, so the banner looks like your site. You can still change them." />
      ) : null}
      <S.SwatchGrid>
        <ColorField
          label={t('Primary')}
          value={palette.primary}
          onChange={(v) => onPalette('primary', v)}
          disabled={locked}
        />
        <ColorField
          label={t('Accent')}
          value={palette.accent}
          onChange={(v) => onPalette('accent', v)}
          disabled={locked}
        />
        <ColorField label={t('Background')} value={palette.bg} onChange={(v) => onPalette('bg', v)} disabled={locked} />
        <ColorField label={t('Text')} value={palette.text} onChange={(v) => onPalette('text', v)} disabled={locked} />
      </S.SwatchGrid>
    </S.Section>
  );
};

export default BrandThemeFields;
